import { findPath } from "src/components/TreeStd";
import { TreeLikeData } from "src/types/TreeLikeData";
import { findNode } from "src/common/findNode";
import { EntityFilterTreeData } from "../AttsTree/types";
import { ZEntityFilterStateConfig } from "../EntityFiltersStateStrategy";
import { EntityFilterRestriction } from "../EntityList/types";
import { makeFilterRestrictionNodeKey } from "./makeFilterRestrictionNodeKey";

type EntityFilterStateData = ZEntityFilterStateConfig["data"];

/**
 * Цепочка узлов от корня к первому выбранному ключу (по факту — один «луч» в дереве).
 * Берём findPath от первого selected: путь от листа к корню, разворачиваем и мапим в узлы.
 */
const traceNodesForSelectedKeys = (
  treeData: TreeLikeData<EntityFilterTreeData>[],
  selected: string[],
) =>
  findPath(treeData, selected[0] ?? "")
    ?.reverse()
    .map((key) => findNode((node) => node.key === key, treeData)) ?? [];

/**
 * «Чистый» сброс состояния дерева в user-preferences нужен, если нет якоря на restriction:
 * нет первого узла в трейсе, либо его key не совпадает с ключом узла restriction
 * (пользователь ещё не на том первом уровне иерархии).
 */
const shouldApplyCleanRestrictionState = (
  data: EntityFilterStateData,
  restriction: EntityFilterRestriction,
): boolean => {
  const { treeData, selected } = data.treeSlected;

  const trace = traceNodesForSelectedKeys(treeData, selected);
  const first = trace[0];
  if (!first) {
    return true;
  }
  return String(first.key) !== makeFilterRestrictionNodeKey(restriction);
};

/** Одна корневая нода restriction — по смыслу `withFilterRestriction` для `hierarchicalFilter`. */
const makeCleanRestrictionTreeRoot = (
  restriction: EntityFilterRestriction,
): TreeLikeData<EntityFilterTreeData> => {
  const { filterAttrId, filterValueStr } = restriction;
  return {
    key: makeFilterRestrictionNodeKey(restriction),
    title: filterValueStr,
    attrId: filterAttrId,
    filterValue: {
      name: filterValueStr,
      toSearch: [filterValueStr],
    },
    isLeaf: false,
  };
};

/**
 * Подготовка сохранённого состояния сайдера под restriction из меню/URL.
 * При необходимости подменяет treeData/selected/order на один корень restriction;
 * иначе возвращает исходный объект без копирования.
 */
export const withEntityFilterStateDataFilterRestriction = (
  data: EntityFilterStateData,
  restriction?: EntityFilterRestriction,
): EntityFilterStateData => {
  if (!restriction) {
    return data;
  }

  if (!shouldApplyCleanRestrictionState(data, restriction)) {
    return data;
  }

  /* В чеклисте порядка атрибутов id совпадает с ключом ноды дерева для этого restriction. */
  const attrId = String(restriction.filterAttrId);
  const restrictionNode = makeCleanRestrictionTreeRoot(restriction);
  return {
    ...data,
    attsOrder: [attrId, ...data.attsOrder.filter((id) => id !== attrId)],
    attsSelected: [attrId, ...data.attsSelected.filter((id) => id !== attrId)],
    treeSlected: {
      ...data.treeSlected,
      treeData: [restrictionNode],
      selected: [String(restrictionNode.key)],
      expanded: [],
    },
  };
};
